import type { AppInvitationOutcome } from './send-invitation';

/**
 * Invite Form Model
 *
 * The invite dialog's state and its transitions, as pure functions — shared by
 * the web `InviteModal` and the mobile `/invite` card so both read a send the
 * same way. Nothing here knows about React or the network; the send itself is
 * `sendAppInvitation`.
 */

// =============================================================================
// STATE
// =============================================================================

export interface InviteFormState {
  email: string;
  isSending: boolean;
  /** Belongs under the email field; null when there is nothing to fix. */
  fieldError: string | null;
  /** The last send failed on the server's side: show the alert. */
  hasFailed: boolean;
}

/** A fresh dialog: reopening never resurrects the last attempt. */
export const INVITE_FORM_DEFAULTS: InviteFormState = {
  email: '',
  isSending: false,
  fieldError: null,
  hasFailed: false,
};

/** Which face the dialog shows. With none left there is no form to fail. */
export type InviteDialogView = 'form' | 'spent';

export function inviteDialogView(left: number): InviteDialogView {
  return left === 0 ? 'spent' : 'form';
}

// =============================================================================
// TRANSITIONS
// =============================================================================

/** The address changed: any refusal was about the old one. */
export function editInviteEmail(
  state: InviteFormState,
  email: string
): InviteFormState {
  return { ...state, email, fieldError: null };
}

/** A send went out: clear the last answer. */
export function startInviteSend(state: InviteFormState): InviteFormState {
  return { ...state, isSending: true, fieldError: null, hasFailed: false };
}

/**
 * Fold a send's answer back in. `sent` and `spent` leave the form as it was:
 * the dialog closes on a send, and on a spent quota the re-read user's count
 * already turns it to its all-sent view.
 */
export function applyInviteOutcome(
  state: InviteFormState,
  outcome: AppInvitationOutcome
): InviteFormState {
  const settled = { ...state, isSending: false };

  switch (outcome.kind) {
    case 'field':
      return { ...settled, fieldError: outcome.message };
    case 'failed':
      return { ...settled, hasFailed: true };
    case 'sent':
    case 'spent':
      return settled;
  }
}
